import { useState } from "react";
import { isDefaultStatusFilter } from "@/hooks/usePriorityListFilters";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Search, Filter, Users, User as UserIcon, Check, ChevronsUpDown, ListFilter, X, Inbox } from "lucide-react";
import { PppMultiSelect } from "./PppMultiSelect";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { PriorityStatus } from "@/types";
import { PRIORITY_STATUS_LABEL, PRIORITY_STATUS_TOOLTIP } from "./priorityCardTokens";

type ViewMode = "all" | "mine";

interface User {
    pid: string;
    fullName: string;
}

interface Team {
    pid: string;
    name: string;
}


interface PriorityFiltersProps {
    search: string;
    onSearchChange: (value: string) => void;
    typeFilter: string;
    onTypeFilterChange: (value: string) => void;
    statusFilter: string[];
    onStatusFilterChange: (value: string[]) => void;
    userFilter: string;
    onUserFilterChange: (value: string) => void;
    teamFilter: string;
    onTeamFilterChange: (value: string) => void;
    users: User[];
    teams: Team[];
    pppFilter: string[];
    onPppFilterChange: (value: string[]) => void;
    candidatesOnly?: boolean;
    onCandidatesOnlyChange?: (value: boolean) => void;
    onUserSearchChange: (value: string) => void;
    onClearAll?: () => void;
    viewMode?: ViewMode;
    onViewModeChange?: (mode: ViewMode) => void;
}

export const PriorityFilters = ({
    search,
    onSearchChange,
    typeFilter,
    onTypeFilterChange,
    statusFilter,
    onStatusFilterChange,
    userFilter,
    onUserFilterChange,
    teamFilter,
    onTeamFilterChange,
    users = [],
    teams = [],
    pppFilter,
    onPppFilterChange,
    candidatesOnly = false,
    onCandidatesOnlyChange,
    onUserSearchChange,
    onClearAll,
    viewMode = "all",
    onViewModeChange,
}: PriorityFiltersProps) => {
    const [openUser, setOpenUser] = useState(false);
    const [openStatus, setOpenStatus] = useState(false);

    const hasActiveFilters =
        search !== "" ||
        typeFilter !== "all" ||
        !isDefaultStatusFilter(statusFilter) ||
        userFilter !== "all" ||
        teamFilter !== "all" ||
        pppFilter.length > 0 ||
        candidatesOnly ||
        viewMode === "mine";

    const activeFilterCount = [
        search !== "",
        typeFilter !== "all",
        !isDefaultStatusFilter(statusFilter),
        userFilter !== "all",
        teamFilter !== "all",
        pppFilter.length > 0,
        candidatesOnly,
        viewMode === "mine",
    ].filter(Boolean).length;

    // Block Int / Block Ext are adjacent so the pair reads as one distinction.
    const statusOrder: PriorityStatus[] = ["focus", "queue", "done", "blocked", "waiting", "on-hold"];
    const statusOptions = statusOrder.map((value) => ({
        value,
        label: PRIORITY_STATUS_LABEL[value],
        description: PRIORITY_STATUS_TOOLTIP[value],
    }));

    const toggleStatus = (status: string) => {
        const newStatuses = statusFilter.includes(status)
            ? statusFilter.filter(s => s !== status)
            : [...statusFilter, status];
        onStatusFilterChange(newStatuses);
    };


    return (
        <div className="mb-6 space-y-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                {onViewModeChange && (
                    <div className="flex shrink-0 rounded-md border">
                        <Button
                            variant={viewMode === "all" ? "default" : "ghost"}
                            size="sm"
                            className={cn("gap-1.5 rounded-r-none border-0", viewMode !== "all" && "text-muted-foreground")}
                            onClick={() => onViewModeChange("all")}
                        >
                            <Users className="h-3.5 w-3.5" /> All
                        </Button>
                        <Button
                            variant={viewMode === "mine" ? "default" : "ghost"}
                            size="sm"
                            className={cn("gap-1.5 rounded-l-none border-0", viewMode !== "mine" && "text-muted-foreground")}
                            onClick={() => onViewModeChange("mine")}
                        >
                            <UserIcon className="h-3.5 w-3.5" /> Mine
                        </Button>
                    </div>
                )}
                <div className="relative min-w-[200px] flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search by name or code..."
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
                    <Select value={typeFilter} onValueChange={onTypeFilterChange}>
                        <SelectTrigger className="w-full sm:w-[130px]">
                            <Filter className="mr-2 h-4 w-4" />
                            <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="SHP">SHP</SelectItem>
                            <SelectItem value="WKR">WKR</SelectItem>
                            <SelectItem value="DAS">DAS</SelectItem>
                        </SelectContent>
                    </Select>

                    <Popover open={openStatus} onOpenChange={setOpenStatus}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={openStatus}
                                className="w-full justify-between sm:w-[200px]"
                            >
                                <div className="flex items-center gap-2 truncate">
                                    <ListFilter className="h-4 w-4 shrink-0" />
                                    <span className="truncate">
                                        {isDefaultStatusFilter(statusFilter)
                                            ? "Active (not done)"
                                            : statusFilter.length === 0
                                            ? "All Status"
                                            : statusFilter.length === 1
                                            ? statusOptions.find(s => s.value === statusFilter[0])?.label
                                            : `${statusFilter.length} selected`}
                                    </span>
                                </div>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0">
                            <Command>
                                <CommandList>
                                    <CommandGroup>
                                        {statusOptions.map((status) => (
                                            <CommandItem
                                                key={status.value}
                                                onSelect={() => toggleStatus(status.value)}
                                                title={status.description}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        statusFilter.includes(status.value) ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                {status.label}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    <Popover open={openUser} onOpenChange={setOpenUser}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={openUser}
                                className="w-full justify-between sm:w-[200px]"
                            >
                                <div className="flex items-center truncate">
                                    <UserIcon className="mr-2 h-4 w-4 shrink-0" />
                                    <span className="truncate">
                                        {userFilter && userFilter !== "all"
                                            ? users.find((user) => user.pid === userFilter)?.fullName
                                            : "All Users"}
                                    </span>
                                </div>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[200px] p-0">
                            <Command shouldFilter={false}>
                                <CommandInput
                                    placeholder="Search user..."
                                    onValueChange={onUserSearchChange}
                                />
                                <CommandList>
                                    <CommandEmpty>No user found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="all"
                                            onSelect={() => {
                                                onUserFilterChange("all");
                                                setOpenUser(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    userFilter === "all" ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            All Users
                                        </CommandItem>
                                        {users.map((user) => (
                                            <CommandItem
                                                key={user.pid}
                                                value={user.fullName}
                                                onSelect={() => {
                                                    onUserFilterChange(user.pid);
                                                    setOpenUser(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        userFilter === user.pid ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                {user.fullName}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    <Select value={teamFilter} onValueChange={onTeamFilterChange}>
                        <SelectTrigger className="w-full sm:w-[160px]">
                            <Users className="mr-2 h-4 w-4" />
                            <SelectValue placeholder="Team" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Teams</SelectItem>
                            {teams.map((team) => (
                                <SelectItem key={team.pid} value={team.pid}>
                                    {team.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <PppMultiSelect value={pppFilter} onChange={onPppFilterChange} placeholder="All PPPs" mode="filter" className="w-full sm:w-[200px]" />
                    {onCandidatesOnlyChange && (
                        <Button
                            variant={candidatesOnly ? "default" : "outline"}
                            className="w-full gap-2 sm:w-auto"
                            onClick={() => onCandidatesOnlyChange(!candidatesOnly)}
                        >
                            <Inbox className="h-4 w-4" />
                            Candidates only
                        </Button>
                    )}
                </div>
                {hasActiveFilters && onClearAll && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClearAll}
                        className="shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
                    >
                        <X className="h-3.5 w-3.5" />
                        Clear
                        {activeFilterCount > 1 && (
                            <Badge variant="secondary" className="ml-0.5 h-4 min-w-4 px-1 text-[10px]">
                                {activeFilterCount}
                            </Badge>
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
};
